import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  servicesPageContent,
  homeStats,
  homeFaqItems,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
  CONTACT_EMAIL,
  SHOW_TECHNOLOGY_PARTNERS,
} from "../data/constants";
import AnimatedCounter from "../components/AnimatedCounter";
import HomeSectionHead from "../components/HomeSectionHead";
import { HomeCustomersSection } from "../components/HomeLogoSections";
import IndiaCustomerMapSection from "../components/IndiaCustomerMapSection";
import SiteCta from "../components/SiteCta";
import HomeHeroOrb from "../components/HomeHeroOrb";
import ServiceDeliverStack from "../components/ServiceDeliverStack";
import HomeEcosystemHub from "../components/HomeEcosystemHub";
import { submitSaleInquiryDb } from "../api/cmsApi";

const POPUP_STORAGE_KEY = "emicron_home_popup_shown";

const baseUrl = process.env.PUBLIC_URL || "";

const whatWeDoItems = [
  {
    icon: "bi-hdd-stack-fill",
    title: "Enterprise Computing, Storage & Virtualization",
    subtitle: "Empower your business with high-performance computing, scalable storage, and next-gen virtualization.",
    bullets: [
      "Data Center Solutions – Rack servers, blade servers, converged and hyper-converged infrastructure.",
      "Enterprise Storage & Virtualization – SAN, NAS, NVMe, SSD storage, VMware, Hyper-V, Kubernetes.",
      "Hybrid & Cloud Computing – On-premises, private, public, and hybrid cloud deployments.",
      "High-Performance Computing (HPC) – AI/ML workloads, GPU computing, edge processing.",
    ],
  },
  {
    icon: "bi-diagram-3-fill",
    title: "Networking, IoT & Collaboration",
    subtitle: "Enable seamless, intelligent, and secure connectivity with cutting-edge networking and IoT solutions.",
    bullets: [
      "Enterprise Networking – Switching, routing, SD-WAN, Access-Point, network segmentation.",
      "IoT & Edge Computing – Smart sensors, industrial automation, M2M communication.",
      "Unified Communication & Collaboration – VoIP, video conferencing, SIP solutions, Microsoft Teams, Zoom.",
      "AI-Powered Security & Surveillance – Smart monitoring, automated threat detection.",
    ],
  },
  {
    icon: "bi-shield-lock-fill",
    title: "Cybersecurity & Compliance",
    subtitle: "Protect digital assets with multi-layered security frameworks and proactive threat intelligence.",
    bullets: [
      "Network Security & Threat Prevention – Next-Gen Firewalls (NGFW), IDS/IPS, UTM solutions.",
      "Identity & Access Management – Multi-Factor Authentication (MFA), Zero Trust, SSO.",
      "Cloud & Endpoint Protection – EDR, XDR, SIEM, SOAR solutions.",
      "Compliance & Risk Management – GDPR, ISO 27001, PCI-DSS, SOC2 compliance.",
    ],
  },
  {
    icon: "bi-cloud-fill",
    title: "Cloud, DevOps & Digital Transformation",
    subtitle: "Accelerate business agility with cloud-driven IT modernization and DevOps automation.",
    bullets: [
      "Cloud Solutions – Microsoft Azure, AWS, Google Cloud, private and hybrid cloud.",
      "Cloud Migration & Automation – Infrastructure-as-Code (Terraform, Ansible), Kubernetes, Docker.",
      "AI, Big Data & Analytics – Data warehousing, Apache Hadoop, Spark, AI-driven insights.",
      "Disaster Recovery & Backup Solutions – Veeam, Acronis, Commvault, data replication & failover.",
    ],
  },
  {
    icon: "bi-robot",
    title: "EMERGING TECHNOLOGY - AI, Automation & Business Intelligence",
    subtitle: "Enhance business efficiency with AI-powered automation and intelligent analytics.",
    bullets: [
      "AI & Machine Learning Solutions – NLP, deep learning, TensorFlow, PyTorch.",
      "Robotic Process Automation (RPA) – UiPath, Power Automate, workflow automation.",
      "Industrial IoT & Smart Infrastructure – Predictive maintenance, SCADA, IoT gateways.",
      "Business Intelligence & Data Science – Power BI, Tableau, Looker, AI-powered dashboards.",
    ],
  },
  {
    icon: "bi-headset",
    title: "Managed IT Services & IT Outsourcing",
    subtitle: "Ensure 24/7 IT efficiency with comprehensive managed IT services and integration support.",
    bullets: [
      "End-to-End IT Support & Monitoring – IT operations management, NOC, SOC.",
      "Cloud & IT Infrastructure Management – SLA-based IT monitoring and optimization.",
      "IT Outsourcing & Device Management – Workstations, endpoints, endpoint security.",
      "Enterprise IT Consulting & System Integration – ITSM, ITIL-based managed services.",
    ],
  },
];

const testimonials = [
  { name: "Rahul S.", role: "IT Head, Manufacturing", text: "Emicron delivered a robust network and security setup. Their team is professional and responsive. Highly recommended.", rating: 5 },
  { name: "Priya M.", role: "CTO, Tech Startup", text: "From cloud migration to ongoing support, Emicron has been a reliable partner. Our infrastructure is now scalable and secure.", rating: 5 },
  { name: "Vikram K.", role: "Director, Healthcare", text: "We needed compliance and security—Emicron understood our needs and delivered. Excellent expertise and service.", rating: 5 },
  { name: "Anita R.", role: "Operations Head, Retail", text: "Smooth implementation and great support. Our systems have never been more reliable.", rating: 5 },
  { name: "Rajesh P.", role: "CFO, Logistics", text: "Cost-effective solutions without compromising on quality. Emicron understands business needs.", rating: 5 },
  { name: "Kavita S.", role: "IT Manager, Education", text: "From network design to security, everything was delivered on time. A trusted technology partner.", rating: 5 },
];

// Ensure testimonials are unique by text to avoid duplicate reviews showing
const uniqueTestimonials = (() => {
  const seen = new Set();
  const out = [];
  for (const t of testimonials) {
    const key = (t.text || '').trim();
    if (!key) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
})();

export default function HomePage() {
  const navigate = useNavigate();
  const goToSolution = (slug) => navigate(`/solutions/${slug}`);
  const handleSolutionKeyDown = (e, slug) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToSolution(slug);
    }
  };
  const [whatWeDoOpen, setWhatWeDoOpen] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupForm, setPopupForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [popupSent, setPopupSent] = useState(false);
  const [popupSubmitting, setPopupSubmitting] = useState(false);
  const [popupError, setPopupError] = useState("");
  const [faqOpen, setFaqOpen] = useState({});
  const [activeServiceStep, setActiveServiceStep] = useState(0);
  const [serviceScrollProgress, setServiceScrollProgress] = useState(0);
  const serviceStepRefs = useRef([]);

  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  const renderFaqAnswer = (item) => {
    if (item.q === "How can I get a consultation?") {
      return (
        <>
          You can reach us via the{" "}
          <Link to="/contact" className="faq-answer-link">
            Contact page
          </Link>
          , call us at{" "}
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="faq-answer-link">
            {CONTACT_PHONE}
          </a>
          , or email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="faq-answer-link">
            {CONTACT_EMAIL}
          </a>
          . We respond to all inquiries within 24 hours on business days.
        </>
      );
    }
    return item.a;
  };

  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    setPopupError("");
    setPopupSubmitting(true);
    try {
      await submitSaleInquiryDb({
        name: popupForm.name,
        email: popupForm.email,
        company: popupForm.company,
        phone: popupForm.phone,
        message: popupForm.message || "Home page consultation request",
        source: "home-popup",
      });
      setPopupSent(true);
      setTimeout(() => {
        setShowPopup(false);
        setPopupSent(false);
      }, 1500);
    } catch (err) {
      setPopupError(err?.message || "Could not submit. Please try again.");
    } finally {
      setPopupSubmitting(false);
    }
  };

  const maybeShowPopup = useCallback(() => {
    try {
      if (sessionStorage.getItem(POPUP_STORAGE_KEY)) return;
      setShowPopup(true);
      sessionStorage.setItem(POPUP_STORAGE_KEY, "1");
    } catch (_) {
      setShowPopup(true);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(maybeShowPopup, 20000);

    const onScroll = () => {
      const half = window.innerHeight * 0.5;
      if (window.scrollY >= half) {
        maybeShowPopup();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [maybeShowPopup]);

  const toggleWhatWeDo = (i) => {
    setWhatWeDoOpen((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const scrollServices = servicesPageContent.slice(0, 6);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    let raf = 0;

    const getStickyTop = () => {
      const section = document.querySelector(".service-scroll-section");
      if (!section) return 108;
      const raw = getComputedStyle(section).getPropertyValue("--service-sticky-top").trim();
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : 108;
    };

    const updateActiveStep = () => {
      const nodes = serviceStepRefs.current.filter(Boolean);
      if (!nodes.length) return;

      const stage = document.querySelector(".service-scroll-stage");
      if (!stage) return;

      const stageRect = stage.getBoundingClientRect();
      const viewport = window.innerHeight;
      const stickyTop = getStickyTop();
      const totalSteps = nodes.length;

      if (stageRect.top > viewport) return;

      if (stageRect.bottom <= stickyTop + 120) {
        setActiveServiceStep(totalSteps - 1);
        setServiceScrollProgress(1);
        return;
      }

      const scrollSpan = Math.max(stage.offsetHeight - (viewport - stickyTop), 1);
      const scrolled = stickyTop - stageRect.top;
      const progress = Math.min(Math.max(scrolled / scrollSpan, 0), 1);
      const idx = Math.min(Math.floor(progress * totalSteps), totalSteps - 1);
      setActiveServiceStep(idx);
      setServiceScrollProgress(progress);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateActiveStep);
    };

    updateActiveStep();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    mq.addEventListener("change", updateActiveStep);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      mq.removeEventListener("change", updateActiveStep);
    };
  }, [scrollServices.length]);

  return (
    <div className="home-page">
      {/* HERO */}
      <section className="home-hero-section animate-on-scroll">
        <div className="home-hero-bg" aria-hidden="true">
          <div className="home-hero-ambient home-hero-ambient--green" />
          <div className="home-hero-ambient home-hero-ambient--orange" />
          <div className="home-hero-waves">
            <span className="home-hero-wave-ring home-hero-wave-ring--1" />
            <span className="home-hero-wave-ring home-hero-wave-ring--2" />
            <span className="home-hero-wave-ring home-hero-wave-ring--3" />
          </div>
          <div className="home-hero-grid" />
          <div className="home-hero-spotlight" />
          <div className="home-hero-orb">
            <HomeHeroOrb />
          </div>
        </div>
        <div className="container home-hero-inner">
          <div className="home-hero-copy">
            <div className="home-hero-content">
            <div className="home-hero-badge">
              <span className="home-hero-badge-dot" />
              <span>India&apos;s Leading IT Integrator</span>
            </div>
            <h1 className="home-hero-title">
              Build <span className="home-hero-highlight">Digital</span> solutions that power your business
            </h1>
            <p className="home-hero-desc">
              Emicron Techsolutions designs, implements, and manages robust IT solutions — from networking and cybersecurity to cloud and AI — tailored to your business needs.
            </p>
            <div className="home-hero-actions">
              <button type="button" className="home-hero-btn-primary" onClick={() => navigate("/about")}>
                Learn More <i className="bi bi-arrow-right ms-1" />
              </button>
              <button type="button" className="home-hero-btn-outline" onClick={() => navigate("/services")}>
                Explore Solutions
              </button>
            </div>
            <ul className="home-hero-trust">
              <li><i className="bi bi-shield-check" /> Enterprise-grade security</li>
              <li><i className="bi bi-headset" /> 24/7 expert support</li>
              <li><i className="bi bi-geo-alt-fill" /> Pan-India delivery</li>
            </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="home-stats-section animate-on-scroll">
        <div className="container">
          <div className="home-stats-grid">
            {homeStats.map((item) => (
              <div key={item.label} className="home-stat-card">
                <div className="home-stat-icon" aria-hidden="true">
                  <i className={`bi ${item.icon}`} />
                </div>
                <AnimatedCounter
                  value={item.value}
                  suffix={item.suffix}
                  format={item.format}
                  className="home-stat-num"
                  as="div"
                />
                <div className="home-stat-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll Services Showcase */}
      <section className="home-section home-section--wide service-scroll-section">
        <div className="home-full-bleed home-full-bleed--scroll">
          <div className="service-scroll-inner">
            <div className="service-scroll-stage">
              <HomeSectionHead
                label="How We Deliver"
                title="Our Service Capabilities"
                desc="Scroll to explore each core service and how we deliver end-to-end for your environment."
              />
              <div className="service-scroll-sticky-shell">
                <div className="service-scroll-layout">
                  <div className="service-scroll-left-panel">
                    {scrollServices.map((service, idx) => (
                      <article
                        key={`scroll-service-${service.slug || service.title}-${idx}`}
                        className={`service-scroll-step ${idx === activeServiceStep ? "is-active" : ""}`}
                        onClick={() => goToSolution(service.slug)}
                        onKeyDown={(e) => handleSolutionKeyDown(e, service.slug)}
                        role="link"
                        tabIndex={0}
                        aria-label={`View ${service.title} solution`}
                      >
                        <div className="service-scroll-step-num">{String(idx + 1).padStart(2, "0")}</div>
                        <h3>{service.title}</h3>
                        <p>{service.desc}</p>
                      </article>
                    ))}
                  </div>

                  <div className="service-scroll-right">
                    <div className="service-scroll-sticky-card">
                      <div className="service-scroll-visual is-active">
                        <div className="service-scroll-visual-inner">
                          <ServiceDeliverStack
                            activeIndex={activeServiceStep}
                            scrollProgress={serviceScrollProgress}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="service-scroll-triggers" aria-hidden="true">
                {scrollServices.map((service, idx) => (
                  <div
                    key={`scroll-trigger-${service.slug || service.title}-${idx}`}
                    className="service-scroll-trigger"
                    data-step-index={idx}
                    ref={(el) => {
                      serviceStepRefs.current[idx] = el;
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="home-section animate-on-scroll">
        <div className="container">
          <div className="home-panel-card">
            <HomeSectionHead
              label="What We Do"
              title="Emicron Techsolutions Private Limited"
              desc="Empowers your business with end-to-end IT solutions across infrastructure, security, cloud, and automation."
            />
          <div className="what-we-do-accordion">
            {whatWeDoItems.map((item, i) => (
              <div key={i} className={`what-we-do-item${whatWeDoOpen[i] ? " is-open" : ""}`}>
                <button type="button" className="what-we-do-header" onClick={() => toggleWhatWeDo(i)} aria-expanded={!!whatWeDoOpen[i]}>
                  <span className="what-we-do-index">{String(i + 1).padStart(2, "0")}</span>
                  <span className="what-we-do-icon" aria-hidden="true">
                    <i className={`bi ${item.icon}`} />
                  </span>
                  <span className="what-we-do-title">{item.title}</span>
                  <span className="what-we-do-toggle" aria-label={whatWeDoOpen[i] ? "Collapse section" : "Expand section"}>
                    <i className={`bi ${whatWeDoOpen[i] ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true"></i>
                  </span>
                </button>
                {whatWeDoOpen[i] && (
                  <div className="what-we-do-body">
                    <p className="what-we-do-subtitle">{item.subtitle}</p>
                    <ul className="what-we-do-bullets">
                      {item.bullets.map((b, j) => (
                        <li key={j}><i className="bi bi-check2 me-2 text-success"></i>{b}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER NETWORK — map */}
      <section className="home-section home-section--india-map animate-on-scroll">
        <IndiaCustomerMapSection presenceMode />
      </section>
      <HomeCustomersSection />
      {/* Securing Your Future */}
      <section className="home-section animate-on-scroll">
        <div className="container">
          <div className="home-securing-card">
            <div className="home-securing-grid" aria-hidden="true" />
            <div className="row align-items-center g-4">
              <div className="col-lg-8">
                <span className="home-securing-eyebrow">Security & Networking</span>
                <h2 className="home-section-title mb-3">Securing Your Future</h2>
                <p className="mb-4">At EMICRON, we specialize in providing cutting-edge networking and security solutions. Our mission is to safeguard your IT infrastructure, ensuring it&apos;s robust, reliable, and prepared for the future.</p>
                <ul className="home-securing-pills">
                  <li><i className="bi bi-shield-check" aria-hidden="true" /> Advanced Threat Detection</li>
                  <li><i className="bi bi-wifi" aria-hidden="true" /> Seamless Connectivity</li>
                  <li><i className="bi bi-graph-up-arrow" aria-hidden="true" /> Future-Ready Infrastructure</li>
                </ul>
              </div>
              <div className="col-lg-4 text-center">
                <div className="securing-icon-wrap">
                  <span className="securing-icon-ring" aria-hidden="true" />
                  <i className="bi bi-shield-lock-fill"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="home-section home-section--faq-solutions animate-on-scroll">
        <div className="container">
          <div className="home-faq-solutions-panel">
            <div className="home-faq-solutions-panel-head">
              <span className="home-faq-solutions-panel-label">FAQ</span>
              <h2 className="home-faq-solutions-panel-title">Frequently Asked Questions</h2>
              <p className="home-faq-solutions-panel-desc">Quick answers about our services, support, and how we work with your team.</p>
            </div>
            <div className="faq-list home-faq-list">
              {homeFaqItems.map((item, i) => (
                <div key={i} className={`faq-item ${faqOpen[i] ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="faq-question"
                    onClick={() => toggleFaq(i)}
                    aria-expanded={!!faqOpen[i]}
                  >
                    <span className="faq-question-text">{item.q}</span>
                    <span className="faq-icon-wrap" aria-hidden="true">
                      <i className={`bi ${faqOpen[i] ? "bi-dash-lg" : "bi-plus-lg"} faq-icon`}></i>
                    </span>
                  </button>
                  <div className="faq-answer-wrap" aria-hidden={!faqOpen[i]}>
                    <div className="faq-answer">{renderFaqAnswer(item)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-section--wide animate-on-scroll">
        <div className="container">
          <HomeSectionHead label="Testimonials" title="What Our Clients Say" />
        </div>
        <div className="home-full-bleed home-full-bleed--testimonials">
            <div className="testimonials-marquee">
            {[0, 1, 2].map((col) => {
              const colItems = uniqueTestimonials.filter((_, idx) => idx % 3 === col);
              const loopItems = [...colItems, ...colItems];
              const dirClass = col === 1 ? "is-up" : "is-down";
              return (
                <div key={col} className="testimonials-col-wrap">
                  <div className={`testimonials-col ${dirClass}`}>
                    {loopItems.map((t, i) => (
                      <article key={`${col}-${i}`} className="testimonial-quote-card">
                        <div className="testimonial-card-top">
                          <div className="testimonial-avatar" aria-hidden="true">{t.name.charAt(0)}</div>
                          <div className="testimonial-card-meta">
                            <div className="testimonial-quote-meta">{t.name}</div>
                            <div className="testimonial-quote-author">{t.role}</div>
                          </div>
                          <div className="testimonial-stars" aria-label={`${t.rating} out of 5 stars`}>
                            {Array.from({ length: t.rating }).map((_, starIdx) => (
                              <i key={starIdx} className="bi bi-star-fill"></i>
                            ))}
                          </div>
                        </div>
                        <div className="testimonial-quote-mark">“</div>
                        <p className="testimonial-quote-text">{t.text}</p>
                      </article>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {SHOW_TECHNOLOGY_PARTNERS && <HomeEcosystemHub />}

      <SiteCta
        variant="card"
        badge="Get Started"
        title="Ready to transform your"
        accent="IT infrastructure?"
        desc="Partner with Emicron for end-to-end solutions — from assessment and deployment to 24×7 managed operations. Let's discuss what your business needs next."
        buttonText="Schedule a Consultation"
        to="/contact"
        secondaryButtonText="Explore Solutions"
        secondaryTo="/services"
      />

      {/* Home page popup - after 20s or half scroll - with form */}
      {showPopup && (
        <div className="home-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="home-popup-card home-popup-card-with-form" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="home-popup-close" onClick={() => setShowPopup(false)} aria-label="Close"><i className="bi bi-x-lg"></i></button>
            <div className="home-popup-body">
              <h3>Tell us what you need</h3>
              <p className="home-popup-intro">Share a few details and our team will follow up shortly.</p>
              {popupSent ? (
                <div className="alert alert-success mb-0"><i className="bi bi-check-circle me-2"></i>Thank you! We'll contact you soon.</div>
              ) : (
                <form onSubmit={handlePopupSubmit}>
                  {popupError ? <div className="alert alert-danger py-2">{popupError}</div> : null}
                  <div className="mb-2">
                    <input type="text" className="form-control" placeholder="Your Name" value={popupForm.name} onChange={e => setPopupForm({ ...popupForm, name: e.target.value })} required />
                  </div>
                  <div className="mb-2">
                    <input type="text" className="form-control" placeholder="Company Name" value={popupForm.company} onChange={e => setPopupForm({ ...popupForm, company: e.target.value })} required />
                  </div>
                  <div className="mb-2">
                    <input type="email" className="form-control" placeholder="Email" value={popupForm.email} onChange={e => setPopupForm({ ...popupForm, email: e.target.value })} required />
                  </div>
                  <div className="mb-2">
                    <input type="tel" className="form-control" placeholder="Phone" value={popupForm.phone} onChange={e => setPopupForm({ ...popupForm, phone: e.target.value })} />
                  </div>
                  <div className="mb-3">
                    <textarea className="form-control" rows={3} placeholder="Message (optional)" value={popupForm.message} onChange={e => setPopupForm({ ...popupForm, message: e.target.value })} style={{ resize: "none" }} />
                  </div>
                  <button type="submit" className="btn-primary-custom w-100" disabled={popupSubmitting}>
                    {popupSubmitting ? "Submitting..." : <>Submit <i className="bi bi-send"></i></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
